<template>
  <canvas ref="canvas"/>
</template>

<script>
import Chart from 'chart.js/auto'

export default {
  name: 'InvoiceDistributionChart',
  props: {
    customers: {
      type: Array,
      required: true
    },
    showWithClipping: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      chart: null
    }
  },
  mounted() {
    this.createChart()
  },
  watch: {
    customers: {
      handler() {
        this.updateChart()
      },
      deep: true
    },
    showWithClipping() {
      this.updateChart()
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy()
    }
  },
  methods: {
    createChart() {
      const ctx = this.$refs.canvas.getContext('2d')

      this.chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.getBinLabels(),
          datasets: [
            {
              label: 'Number of Customers',
              data: this.getInvoiceDistributionData(),
              backgroundColor: 'rgba(54, 162, 235, 0.6)',
              borderColor: 'rgba(54, 162, 235, 1)',
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: 'Invoice Count Distribution'
            },
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const binIndex = context.dataIndex
                  const avgNet = this.invoiceBins && this.invoiceBins[binIndex]
                    ? this.invoiceBins[binIndex].averageNet
                    : 0
                  const totalNet = this.invoiceBins && this.invoiceBins[binIndex]
                    ? this.invoiceBins[binIndex].totalNet
                    : 0
                  const lines = [`${context.parsed.y} customers`]
                  if (avgNet > 0) {
                    lines.push(`Average: $${(avgNet / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`)
                    lines.push(`Total: $${(totalNet / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`)
                  }
                  return lines
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Invoice Count Range'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Number of Customers'
              },
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            }
          }
        }
      })
    },

    updateChart() {
      if (this.chart) {
        this.chart.data.labels = this.getBinLabels()
        this.chart.data.datasets[0].data = this.getInvoiceDistributionData()
        this.chart.update()
      }
    },

    getInvoiceDistributionData() {
      const invoices = this.customers.map(customer =>
        customer.stats && typeof customer.stats.invoiceCount === 'number' ? customer.stats.invoiceCount : 0
      ).filter(count => count > 0)

      const bins = this.createBins(invoices)
      this.invoiceBins = bins // Store for tooltip access
      return bins.map(bin => bin.count)
    },

    getBinLabels() {
      const invoices = this.customers.map(customer =>
        customer.stats && typeof customer.stats.invoiceCount === 'number' ? customer.stats.invoiceCount : 0
      ).filter(count => count > 0)

      const bins = this.createBins(invoices)
      return bins.map(bin => `${Math.round(bin.min)}-${Math.round(bin.max)}`)
    },

    createBins(values) {
      if (values.length === 0) return []

      const validValues = values.filter(value => value > 0)
      if (validValues.length === 0) return []

      let clippedValues = validValues

      // Remove outliers using IQR method only if clipping is enabled
      if (this.showWithClipping) {
        const sortedValues = [...validValues].sort((a, b) => a - b)
        const q1 = sortedValues[Math.floor(sortedValues.length * 0.25)]
        const q3 = sortedValues[Math.floor(sortedValues.length * 0.75)]
        const iqr = q3 - q1
        const lowerBound = q1 - 1.5 * iqr
        const upperBound = q3 + 1.5 * iqr

        clippedValues = validValues.filter(value => value >= lowerBound && value <= upperBound)
      }

      if (clippedValues.length === 0) {
        // Fallback to original data if all values are outliers
        const minValue = Math.min(...validValues)
        const maxValue = Math.max(...validValues)
        return [{
          min: minValue,
          max: maxValue,
          count: validValues.length,
          averageNet: this.calculateFallbackAverageNet()
        }]
      }

      const minValue = Math.min(...clippedValues)
      const maxValue = Math.max(...clippedValues)
      const binCount = Math.min(8, clippedValues.length) // Max 8 bins, or number of values if less

      if (minValue === maxValue) {
        return [{
          min: minValue,
          max: maxValue,
          count: clippedValues.length,
          averageNet: this.calculateFallbackAverageNet()
        }]
      }

      const binWidth = (maxValue - minValue) / binCount
      const bins = []

      for (let i = 0; i < binCount; i++) {
        const binMin = minValue + (i * binWidth)
        const binMax = minValue + ((i + 1) * binWidth)

        const valuesInBin = clippedValues.filter(value => {
          return i === binCount - 1 ?
            (value >= binMin && value <= binMax) :
            (value >= binMin && value < binMax)
        })

        const count = valuesInBin.length
        let averageNet = 0
        let totalNet = 0

        if (count > 0) {
          // Find customers whose invoice counts fall within this bin range
          const customersInBin = this.customers.filter(customer => {
            if (!customer.stats || typeof customer.stats.invoiceCount !== 'number' || customer.stats.invoiceCount <= 0) {
              return false
            }
            
            const invoiceCount = customer.stats.invoiceCount
            return i === binCount - 1 ?
              (invoiceCount >= binMin && invoiceCount <= binMax) :
              (invoiceCount >= binMin && invoiceCount < binMax)
          })
          
          const netValues = customersInBin
            .filter(customer => customer.stats && typeof customer.stats.net === 'number')
            .map(customer => customer.stats.net)
          
          if (netValues.length > 0) {
            totalNet = netValues.reduce((sum, val) => sum + val, 0)
            averageNet = totalNet / netValues.length
          }
        }

        bins.push({
          min: binMin,
          max: binMax,
          count: count,
          averageNet: averageNet,
          totalNet: totalNet
        })
      }

      return bins
    },

    calculateFallbackAverageNet() {
      // Calculate overall average net value of all customers
      const netValues = this.customers
        .filter(customer => customer.stats && typeof customer.stats.net === 'number')
        .map(customer => customer.stats.net)

      return netValues.length > 0 ? netValues.reduce((sum, val) => sum + val, 0) / netValues.length : 0
    },

    calculateAverageNetForBin(binMin, binMax, isLastBin) {
      // Find customers whose invoice counts fall within this bin range
      const customersInBin = this.customers.filter(customer => {
        if (!customer.stats || typeof customer.stats.invoiceCount !== 'number' || customer.stats.invoiceCount <= 0) {
          return false
        }

        const invoiceCount = customer.stats.invoiceCount
        return isLastBin ?
          (invoiceCount >= binMin && invoiceCount <= binMax) :
          (invoiceCount >= binMin && invoiceCount < binMax)
      })

      if (customersInBin.length === 0) return 0

      // Calculate average net value for customers in this bin
      const netValues = customersInBin
        .filter(customer => customer.stats && typeof customer.stats.net === 'number')
        .map(customer => customer.stats.net)

      return netValues.length > 0 ? netValues.reduce((sum, val) => sum + val, 0) / netValues.length : 0
    }
  }
}
</script>

<style scoped>
</style>